
import { readContract, writeContract } from '@wagmi/core'
import { bsc } from 'wagmi/chains'
import { config } from '../components/Web3Provider'
import { TOP1_TOKEN } from './token'
import { erc20ABI } from './abi/erc20'
import { toast } from "sonner"

// Check if user has $TOP1 token by checking their balance
export const checkTokenOwnership = async (address: `0x${string}`) => {
  try {
    console.log('Checking $TOP1 balance for:', address)
    
    // Read the token balance using ERC20 balanceOf function
    const balance = await readContract(config, {
      address: TOP1_TOKEN.address as `0x${string}`,
      abi: erc20ABI,
      functionName: 'balanceOf',
      args: [address],
      chainId: bsc.id,
    })
    
    console.log('$TOP1 balance result:', balance)
    
    // Return true if balance > 0
    return typeof balance === 'bigint' && balance > 0n
  } catch (error) {
    console.error('Error checking $TOP1 token balance:', error)
    return false
  }
}

// Burn TOP1 tokens by sending them to a dead address with proper validation
export const burnTOP1Tokens = async (amount: number, address: string): Promise<{ success: boolean; txHash?: string; error?: string }> => {
  try {
    console.log(`Burning ${amount} TOP1 tokens from ${address}`);
    
    // First check if user has enough balance
    const balance = await readContract(config, {
      address: TOP1_TOKEN.address as `0x${string}`,
      abi: erc20ABI,
      functionName: 'balanceOf',
      args: [address as `0x${string}`],
      chainId: bsc.id,
    }) as bigint;
    
    const amountInWei = BigInt(amount) * BigInt(10 ** 18);
    
    if (balance < amountInWei) {
      return { success: false, error: `Insufficient balance. You have ${Number(balance) / 10**18} TOP1, need ${amount}` };
    }
    
    // Dead address to send tokens to (effectively burning them)
    const deadAddress = "0x000000000000000000000000000000000000dEaD";
    
    console.log('Burning tokens:', {
      from: address,
      to: deadAddress,
      amount: amountInWei.toString(),
      humanAmount: amount
    });
    
    // Call the transfer function to send tokens to the dead address
    const hash = await writeContract(config, {
      address: TOP1_TOKEN.address as `0x${string}`,
      abi: erc20ABI,
      functionName: 'transfer',
      args: [deadAddress as `0x${string}`, amountInWei],
      chain: bsc,
      account: address as `0x${string}`,
    });
    
    console.log(`Burn transaction hash:`, hash);
    toast.success(`Successfully burned ${amount} TOP1 tokens!`);
    
    return { success: true, txHash: hash };
  } catch (error: any) {
    console.error('Error burning TOP1 tokens:', error);
    
    if (error.code === 'ACTION_REJECTED') {
      return { success: false, error: 'Transaction rejected by user' };
    } else if (error.code === 'INSUFFICIENT_FUNDS') {
      return { success: false, error: 'Insufficient funds for gas fees' };
    } else {
      const errorMessage = error.message || 'Unknown error';
      return { success: false, error: `Failed to burn tokens: ${errorMessage.substring(0, 100)}${errorMessage.length > 100 ? '...' : ''}` };
    }
  }
};

// Updated ERC-20 ABI with burn function
export const erc20BurnAbi = [
  ...erc20ABI,
  {
    inputs: [
      { name: 'amount', type: 'uint256' }
    ],
    name: 'burn',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function'
  }
] as const;
